import useMediaQuery from "@core-ui/hooks/useMediaQuery";
import { cn } from "@core-ui/utils/cn";
import { cssMedia } from "@core-ui/utils/cssUtils";
// biome-ignore lint/style/noRestrictedImports: refactor in future releases
import styled from "@emotion/styled";
import { getCommentRangeAtPosition } from "@ext/markdown/elements/comment/edit/logic/getCommentIdAtPosition";
import { useCurrentEditor } from "@tiptap/react";
import { memo, type ReactNode, useCallback, useRef } from "react";

interface BlockCommentViewProps {
	children: ReactNode;
	commentId?: string;
	className?: string;
}

const Wrapper = styled.div`
	height: inherit;
	width: inherit;

	&:first-of-type {
		margin: 0 !important;
	}
`;

const BlockCommentView = ({ children, commentId, className }: BlockCommentViewProps) => {
	const { editor } = useCurrentEditor();
	const isMobile = useMediaQuery(cssMedia.JSnarrow);
	const wrapperRef = useRef<HTMLDivElement>(null);

	const onMouseEnter = useCallback(() => {
		if (!commentId || !editor) return;
		editor.commands.hoverComment(commentId);
	}, [commentId, editor]);

	const onMouseLeave = useCallback(() => {
		if (!editor) return;
		editor.commands.unhoverComment();
	}, [editor]);

	const onClick = useCallback(() => {
		if (!commentId || !editor || editor.isDestroyed || !wrapperRef.current) return;

		const position = editor.view.posAtDOM(wrapperRef.current, 0);
		if (typeof position !== "number") return;

		const range = getCommentRangeAtPosition(editor.state, position, commentId);
		if (!range) return;

		editor.commands.openComment(commentId, range);
	}, [commentId, editor]);

	return (
		<Wrapper
			className={cn("block-comment-view", className)}
			data-comment={commentId ? "true" : "false"}
			data-comment-block={commentId ? "true" : "false"}
			data-comment-id={commentId}
			onClick={commentId ? onClick : undefined}
			onMouseEnter={isMobile ? undefined : onMouseEnter}
			onMouseLeave={isMobile ? undefined : onMouseLeave}
			ref={wrapperRef}
		>
			{children}
		</Wrapper>
	);
};

export default memo(BlockCommentView);
